<template>
    <v-row class="justify-center align-center ma-2">
        <v-card class="col-10">
            <v-card-title>{{ title }}</v-card-title>
            <v-card-text class="px-5">
                <v-col align-self="center">
                    <v-row>
                        <v-text-field :label="$t('Name')" v-model="basket.name" :error-messages="nameErrors"
                            @input="$v.basket.name.$touch()" @blur="$v.basket.name.$touch()"></v-text-field>
                    </v-row>
                    <v-row>
                        <v-autocomplete multiple v-model="basket.contentTypeIDs" :items="contentTypesList"
                            item-text="text" item-value="id" v-bind:label="$t('ContentTypes')">
                            <template v-slot:prepend-item>
                                <v-list-item ripple @click="toggleIncludeAllCTVersions">
                                    <v-list-item-action>
                                        <v-icon color="primary"> {{ icon }} </v-icon>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title> {{ $t("AllVersions") }} </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider class="mt-2"></v-divider>
                            </template>
                        </v-autocomplete>
                    </v-row>
                    <v-row align="center">
                        <v-autocomplete multiple :label="$t('Roles')" v-model="basket.roleTypeIDs" :items="rolesList"
                            item-text="text" item-value="id" :error-messages="rolesErrors"
                            @input="$v.basket.roleTypeIDs.$touch()" @blur="$v.basket.roleTypeIDs.$touch()">
                            <template slot="append-outer">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" color="success" icon @click="onRoleButtonClick">
                                            <v-icon>mdi-table-plus</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ $t("InsertData") }}</span>
                                </v-tooltip>
                            </template>
                        </v-autocomplete>
                    </v-row>
                    <v-row>
                        <v-text-field readonly v-model="basket.displayConditionStr" :label="$t('Condition')"
                            :append-icon="basket.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'"
                            @click:append="editCondition(basket)" :error-messages="conditionErrors"
                            @input="$v.basket.conditionStr.$touch()"
                            @blur="$v.basket.conditionStr.$touch()"></v-text-field>
                    </v-row>
                    <v-row>
                        <v-switch v-model="basket.orderAsc" color="green"
                            :label="$t('SortResultsAscending')"></v-switch>
                    </v-row>
                </v-col>
                <v-overlay :value="loading" :absolute="true">
                    <div class="text-center">
                        <v-progress-circular class="my-4" :size="50" width="5" color="primary"
                            indeterminate></v-progress-circular>
                    </div>
                </v-overlay>
                <SearchCriteriaBuilderModal :ref="conditionModalKey" :contentTypeIDs="basket.contentTypeIDs"
                    @on-condition-saved="onConditionChanged" :canUseVariables="false" />
                <v-row class="justify-end">
                    <v-btn @click="cancel()">{{ $t('Cancel') }}</v-btn>
                    <v-btn @click="saveItem" class="primary">{{ $t('Save') }}</v-btn>
                </v-row>
            </v-card-text>
        </v-card>
    </v-row>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import SearchCriteriaBuilderModal from '@/components/search/SearchCriteriaBuilderModal.vue';
import BasketService from '@/services/BasketService';
import RoleService from '@/services/RoleService';
import Basket from '@/ecm/models/Basket';
import Utils from '@/ecm/common/Utils';
import UtilsSearch from '@/components/search/utilsSearch/UtilsSearch';

export default {
    name: 'Basket',
    components: {
        SearchCriteriaBuilderModal
    },
    async created() {
        this.loading = true;
        this.getRoles();
        await this.getBasket();
        await this.getContentTypes();
        this.$v.$reset();
        this.loading = false;
    },
    data() {
        return {
            rolesList: [],
            contentTypesList: [],
            basket: new Basket(),
            conditionModalKey: 'conditionModalKey',
            includeAllCTVersions: false,
            isNew: true,
            loading: false,
        };
    },
    mixins: [validationMixin],
    validations: {
        basket: {
            name: { required },
            roleTypeIDs: { required },
            conditionStr: { required },
        },
    },
    computed: {
        conditionModalInstance() {
            return this.$refs[this.conditionModalKey];
        },
        title() {
            return (
                this.$t('Basket') + (this.basket.name ? ' - ' + this.basket.name : '')
            );
        },
        nameErrors() {
            const errors = [];
            if (!this.$v.basket.name.$dirty) return errors;
            !this.$v.basket.name.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        conditionErrors() {
            const errors = [];
            if (!this.$v.basket.conditionStr.$dirty) return errors;
            !this.$v.basket.conditionStr.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        rolesErrors() {
            const errors = [];
            if (!this.$v.basket.roleTypeIDs.$dirty) return errors;
            !this.$v.basket.roleTypeIDs.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        icon() {
            if (this.includeAllCTVersions) return 'mdi-close-box';
            return 'mdi-checkbox-blank-outline';
        },
    },
    methods: {
        async getBasket() {
            if (isNaN(this.$route.params.id) || this.$route.params.id < 1) {
                this.basket = new Basket();
            } else {
                this.isNew = false;
                const data = await BasketService.get(this.$route.params.id);
                this.basket = new Basket(data);
                const context = this;
                this.basket.conditionStr = UtilsSearch.SetUserFormatValueIntoConditionStr(data.conditionStr);
                this.basket.displayConditionStr = await UtilsSearch.GetDisplayConditionStr(data.conditionStr, data.defFieldCodes, context);
            }
        },
        toggleIncludeAllCTVersions() {
            this.includeAllCTVersions = !this.includeAllCTVersions;
            this.getContentTypes();
        },
        async getContentTypes() {
            await BasketService.getAvailableContentTypes(
                this.basket.id > 0 ? this.basket.id : null,
                this.includeAllCTVersions
            ).then((data) => {
                this.contentTypesList = data;
            });
        },
        editCondition() {
            this.conditionModalInstance.edit(this.basket.conditionStr);
        },
        onConditionChanged(condition) {
            this.basket.conditionStr = condition.conditionStr;
            this.basket.displayConditionStr = condition.displayConditionStr;
        },
        onRoleButtonClick() {
            this.$router.push('/administration/roles/0');
        },
        getRoles() {
            RoleService.getList().then((data) => {
                this.rolesList = data;
            });
        },
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }
            this.loading = true;
            BasketService.save(this.basket.toDto())
                .then((result) => {
                    if (this.isNew) {
                        Utils.toast.success(
                            this.$t('Basket') + ' ' + this.$t('CreatedSuccessfullyFem')
                        );
                    } else {
                        Utils.toast.success(
                            this.$t('Basket') + ' ' + this.$t('UpdatedSuccessfullyFem')
                        );
                    }
                    this.$router.push('/administration/baskets');
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    this.loading = false;
                });
        },
        cancel() {
            this.$router.push('/administration/baskets');
        },
    },
};
</script>
